import { and, eq } from "drizzle-orm";
import {
  cash_movement,
  payment,
  type PowerSyncDrizzleDB,
  register_session,
} from "@comandero/powersync";
import { liveCashMovementFilters, livePaymentFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import { reconcileCashbox } from "../orders/calc";

/** Abre una sesión de caja con fondo inicial (centavos). */
export async function openRegisterSessionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  openingFloat: number,
  cashierUserId?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(register_session).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    cashier_user_id: cashierUserId ?? null,
    opening_float: Math.trunc(openingFloat),
    status: "open",
    opened_at: now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Registra un movimiento de caja (entrada/salida/venta/devolución). */
export async function addCashMovementMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  registerSessionId: string,
  kind: "in" | "out" | "sale" | "refund",
  amount: number,
  reason?: string,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(cash_movement).values({
    id,
    organization_id: organizationId,
    register_session_id: registerSessionId,
    kind,
    amount: Math.trunc(amount),
    reason: reason ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

const SIGN_BY_KIND: Record<string, number> = { in: 1, sale: 1, refund: -1, out: -1 };

/**
 * Cierra la sesión de caja con el efectivo contado. Calcula el esperado a partir
 * del fondo inicial + pagos en efectivo de la sesión + movimientos manuales, y
 * persiste el conteo. Devuelve la conciliación (esperado/contado/descuadre).
 * El cálculo es puro (calc.reconcileCashbox).
 */
export async function closeRegisterSessionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  registerSessionId: string,
  countedTotal: number,
) {
  const sessionRows = await db
    .select()
    .from(register_session)
    .where(eq(register_session.id, registerSessionId))
    .limit(1);
  const openingFloat = sessionRows[0]?.opening_float ?? 0;

  // Pagos en efectivo de la sesión (ventas).
  const cashPayments = await db
    .select()
    .from(payment)
    .where(
      and(
        eq(payment.register_session_id, registerSessionId),
        eq(payment.method, "cash"),
        ...livePaymentFilters(),
      ),
    );

  // Movimientos manuales (entradas/salidas), excluyendo los "sale" que ya
  // contamos vía payment para no duplicar.
  const movements = await db
    .select()
    .from(cash_movement)
    .where(
      and(eq(cash_movement.register_session_id, registerSessionId), ...liveCashMovementFilters()),
    );

  const signed: number[] = [];
  for (const p of cashPayments) {
    // El efectivo en caja por una venta = importe cobrado + propina en efectivo.
    signed.push((p.amount ?? 0) + (p.tip ?? 0));
  }
  for (const m of movements) {
    if (m.kind === "sale") {
      continue; // evitar doble conteo con payment
    }
    const sign = SIGN_BY_KIND[m.kind ?? "in"] ?? 1;
    signed.push((m.amount ?? 0) * sign);
  }

  const reconciliation = reconcileCashbox(openingFloat, signed, countedTotal);

  await db
    .update(register_session)
    .set({
      counted_total: Math.trunc(countedTotal),
      status: "closed",
      closed_at: nowISO(),
      updated_at: nowISO(),
    })
    .where(
      and(
        eq(register_session.id, registerSessionId),
        eq(register_session.organization_id, organizationId),
      ),
    );

  return reconciliation;
}
